// Состав сделки: подписи и трактовка расхождения. Только оформление — ни одной суммы
// здесь не складывается.
//
// ПОЧЕМУ СУММЫ СЧИТАЕТ ЯДРО, А НЕ ЭТОТ ФАЙЛ. Итог по строкам и разница с суммой сделки
// приезжают из products_api.py уже посчитанными (Decimal в Postgres, а не float в браузере).
// Соблазн «сложить amount по строкам на клиенте» велик, и именно так в этом репозитории
// уже расходились цифры между экраном и разделом: две реализации одной формулы живут
// недолго. Здесь только то, чего ядру знать незачем — слова для человека.

import { fmtMoney } from './format'
import { toNumber } from './money'
import type { СоставСделки, СтепеньРасхождения, ТоварнаяСтрока } from '../api/products'

/**
 * Тип строки. Bitrix отдаёт число, человеческого названия в ответе нет.
 * 1 и 4 — то, что реально встречается на портале; остальное показываем номером,
 * а не выдумываем название.
 */
export function типСтроки(v: number | null): string {
  if (v === 1) return 'товар'
  if (v === 4) return 'услуга'
  return v === null ? '—' : `тип ${v}`
}

/** Количество с единицей измерения: «100 м», «3 шт». */
export function количество(r: ТоварнаяСтрока): string {
  const n = toNumber(r.quantity)
  if (n === null) return '—'
  // Целые показываем без дробной части: «1 шт», а не «1,00 шт».
  const число = Number.isInteger(n) ? String(n) : n.toLocaleString('ru-RU',
    { maximumFractionDigits: 3 })
  return r.measure_name ? `${число} ${r.measure_name}` : число
}

/**
 * Скидка по строке словами. В Bitrix discount_sum — скидка на ЕДИНИЦУ, а discount_rate
 * при discount_type_id = 2 это проценты. Пустую скидку не показываем вовсе: «скидка 0»
 * в каждой строке — шум, у 97 215 строк из 98 580 она нулевая (на 28.07.2026).
 */
export function скидка(r: ТоварнаяСтрока): string | null {
  const сумма = toNumber(r.discount_sum)
  const ставка = toNumber(r.discount_rate)
  if (r.discount_type_id === 2 && ставка) return `−${ставка}%`
  if (сумма) return `−${fmtMoney(сумма, 'RUB')}/ед.`
  return null
}

/** НДС по строке. null в tax_rate — «налог у строки не задан», это НЕ ноль процентов. */
export function ндс(r: ТоварнаяСтрока): string {
  if (r.tax_rate === null || r.tax_rate === undefined) return '—'
  return `${r.tax_rate}%${r.tax_included ? '' : ' сверху'}`
}

/** Что стоит в графе «Номенклатура»: название плюс откуда оно. */
export function номенклатура(r: ТоварнаяСтрока): { name: string; каталог: boolean } {
  return { name: r.product_name || '(без названия)', каталог: !!r.product_id }
}

// ─────────── расхождение суммы сделки с составом ───────────
//
// ЧТО ЗДЕСЬ ИЗМЕНИЛОСЬ И ПОЧЕМУ. Раньше экран сам решал, что за расхождение перед ним,
// по трём признакам (сумма пустая / строк нет / прочее) и любое «прочее» красил в красное
// со словами «Сумма сделки и состав не сходятся». На боевых данных таких сделок
// 8 693 на 2,79 млрд — и 7 058 из них на 1,90 млрд не ошибка вовсе (замер 28.07.2026):
//   • 4 863 — сумма сделки не заполнена; 4 785 из них заведены до 2023 года (на 28.07.2026)
//     в мёртвой воронке, больше половины проиграны. Красить это красным — пугать зря;
//   • 1 251 — состав дешевле суммы: в строки вписывают оборудование, а работы, доставку
//     и монтаж не вписывают. Это способ вести учёт, а не потеря;
//   •   825 — разница меньше 5 %: торг и округление при выставлении.
// А настоящие ошибки в этой куче тонули: 150 задублированных составов (124 — за последний
// год!), 89 сделок с непроставленной ценой, 269 с ценой в валюте при рублёвой сумме.
//
// Поэтому теперь ПРИЧИНУ называет ядро (products_api.py, выражение ПРИЧИНА) — одним SQL
// и для карточки, и для сводки. Здесь только слова и цвет. Повторять правило «во сколько
// раз» на клиенте нельзя: в этом репозитории две реализации одной формулы уже расходились.

export type ВидРасхождения = 'нет' | 'сумма-не-заполнена' | 'состав-пуст' | 'разошлись'

export interface РазборРасхождения {
  вид: ВидРасхождения
  /** Показывать ли расхождение вообще. false — сделка не опрошена, показывать нечего. */
  показывать: boolean
  /** Насколько состав дороже (>0) или дешевле (<0) суммы сделки. */
  delta: number
  /** Строка для человека: что именно не сходится и в какую сторону. */
  подпись: string
  /** Причина от ядра словами. null — расхождения нет либо сделка не опрошена. */
  причина: string | null
  /** Чинить (error), разобраться (check) или не трогать (ok). */
  степень: СтепеньРасхождения | null
  /** Заголовок сообщения: он и решает, тревожиться человеку или нет. */
  заголовок: string
  /** Класс бейджа дизайн-системы: lose — ошибка, work — нейтрально, won — сошлось. */
  вид_бейджа: 'won' | 'lose' | 'work'
  /** Класс блока-сообщения: 'notice err' только для настоящих ошибок. */
  вид_блока: 'notice err' | 'notice'
}

/** Заголовок сообщения по степени. Слова разные не для красоты: «проверьте» и «так и
 *  задумано» — это разные поручения человеку, а красный цвет на «так и задумано» ровно
 *  один раз обесценивает красный цвет вообще. */
const ЗАГОЛОВОК: Record<СтепеньРасхождения, string> = {
  error: 'В составе ошибка.',
  check: 'Сумма и состав расходятся — нужно посмотреть.',
  ok: 'Сумма и состав расходятся, но так ведут учёт.',
}

/**
 * Трактовка расхождения. Число и направление считает ядро, здесь — слова для человека
 * и решение, каким цветом это показывать.
 */
export function разборРасхождения(с: СоставСделки | undefined): РазборРасхождения {
  const пусто: РазборРасхождения = {
    вид: 'нет', показывать: false, delta: 0, причина: null, степень: null,
    заголовок: '', подпись: 'Состав ещё не заливался — сверить сумму не с чем.',
    вид_бейджа: 'work', вид_блока: 'notice',
  }
  if (!с || !с.scanned || !с.diff) return пусто

  const delta = toNumber(с.diff.delta) ?? 0
  const валюта = с.deal.currency || 'RUB'
  const сумма = toNumber(с.deal.opportunity)
  const степень = с.diff.severity
  const причина = с.diff.reason
  const общее = {
    показывать: true, delta, причина, степень,
    заголовок: степень ? ЗАГОЛОВОК[степень] : '',
    // Красным — только то, что действительно надо чинить. Всё прочее нейтрально:
    // человек всё равно увидит цифры, но не будет искать виноватого там, где его нет.
    вид_бейджа: (степень === 'error' ? 'lose' : степень === 'check' ? 'work' : 'work') as
      'won' | 'lose' | 'work',
    вид_блока: (степень === 'error' ? 'notice err' : 'notice') as 'notice err' | 'notice',
  }

  if (!с.diff.mismatch) {
    return {
      ...пусто, показывать: true, delta, вид_бейджа: 'won',
      подпись: 'Итог по строкам совпадает с суммой сделки.',
    }
  }
  if (!сумма) {
    return {
      ...общее, вид: 'сумма-не-заполнена',
      подпись: `Сумма сделки не заполнена, а состав есть — по строкам`
        + ` ${fmtMoney(с.totals.sum, валюта)}.`,
    }
  }
  if (с.totals.lines === 0) {
    return {
      ...общее, вид: 'состав-пуст',
      подпись: `Сумма сделки ${fmtMoney(сумма, валюта)} стоит, а товарных строк нет.`,
    }
  }
  return {
    ...общее, вид: 'разошлись',
    подпись: `Состав ${delta > 0 ? 'дороже' : 'дешевле'} суммы сделки на`
      + ` ${fmtMoney(Math.abs(delta), валюта)}:`
      + ` по строкам ${fmtMoney(с.totals.sum, валюта)},`
      + ` в сделке ${fmtMoney(сумма, валюта)}.`,
  }
}

/**
 * Короткая пометка для заголовка блока.
 *
 * Здесь стоит ПРИЧИНА, а не сумма: «состав задублирован» человек понимает сразу, а
 * «расходится на 3 538 155 ₽» требует открыть сделку и разобраться — и чаще всего
 * выяснить, что разбираться было не в чем.
 */
export function короткоОРасхождении(р: РазборРасхождения, валюта = 'RUB'): string {
  if (!р.показывать) return 'не опрошено'
  if (р.вид === 'нет') return 'сходится'
  if (р.причина) return р.причина
  return `расходится на ${fmtMoney(Math.abs(р.delta), валюта)}`
}

/**
 * Что сказать про расхождение после подписи. Раньше здесь всегда стояло «сумму сделки
 * правят руками, и по составу она не пересчитывается» — верно, но для задублированного
 * состава это неверный совет: там править надо не сумму.
 */
export function чтоДелать(р: РазборРасхождения): string {
  if (р.степень === 'error') {
    return 'Чинится в Bitrix, в самой сделке: зеркало правку не примет и принять не должно.'
  }
  if (р.степень === 'check') {
    return 'Какая цифра верна — вопрос к ответственному: сумму сделки в Bitrix правят'
      + ' руками, и по составу она не пересчитывается.'
  }
  return 'Разбирать это не нужно: на портале так ведут учёт — в состав вписывают'
    + ' не всё, а сумму сделки правят отдельно.'
}

// ─────────── список расхождений по всей базе (экран «Расхождения состава») ───────────
//
// ЧЕГО ЗДЕСЬ НЕТ И НЕ ДОЛЖНО БЫТЬ. Ни списка причин, ни их формулировок: и то и другое
// приезжает от ядра (`summary.by_reason`, поле `why`). Соблазн завести словарь
// «код → короткая подпись» велик — на плитке длинная фраза не помещается, — но такой
// словарь молча устареет в тот день, когда в products_api.py добавят одиннадцатую
// причину: экран покажет пустую подпись там, где ядро прислало готовую. Поэтому кучи
// рисуются строками с текстом ядра, а не плитками со своими словами.
//
// Здесь только то, чего ядру знать незачем: в каком порядке показывать степени, как
// назвать степень на плитке и какое поручение она означает.

/** Порядок степеней на экране: сперва то, что чинят, и только потом то, что не трогают. */
export const ПОРЯДОК_СТЕПЕНЕЙ: СтепеньРасхождения[] = ['error', 'check', 'ok']

/**
 * Степень словами. `плитка` — заголовок счётчика, `что` — поручение человеку.
 *
 * Слова разные не для красоты. «Ошибка» и «так ведут учёт» — это разные поручения, и
 * ровно на этой разнице держится весь разбор: из 8 693 расхождений боевой базы на 28.07.2026
 * чинить надо 702, а 7 058 трогать не нужно вовсе.
 */
export const ПОДПИСЬ_СТЕПЕНИ: Record<СтепеньРасхождения, { плитка: string; что: string }> = {
  error: { плитка: 'Ошибка в данных', что: 'заведено неверно — чинится в Bitrix' },
  check: { плитка: 'Надо посмотреть', что: 'обе цифры правдоподобны, нужен человек' },
  ok: { плитка: 'Так ведут учёт', что: 'разбирать не нужно: способ учёта или шум' },
}

/**
 * Класс значка дизайн-системы по степени.
 *
 * Схема ТА ЖЕ, что у блока «Состав» в карточке сделки (`разборРасхождения`): красный —
 * только настоящая ошибка. Зелёного здесь нет ни у одной строки намеренно — в этом
 * списке каждая строка расходится, и зелёный значок читался бы как «сошлось».
 */
export function классПричины(степень: СтепеньРасхождения | null): 'won' | 'lose' | 'work' {
  return степень === 'error' ? 'lose' : 'work'
}

/**
 * «1 сделка», «2 сделки», «11 сделок», «21 сделка» — иначе везде получается «сделок».
 *
 * Разряды разделяем, как и в суммах: на этом экране счётчики четырёхзначные, и «8693»
 * рядом с «2 786 795 772 ₽» читается медленнее, чем должно.
 */
export function сколькоСделок(n: number): string {
  const число = n.toLocaleString('ru-RU')
  const a = Math.abs(n) % 100
  const b = a % 10
  if (a > 10 && a < 20) return `${число} сделок`
  if (b === 1) return `${число} сделка`
  if (b >= 2 && b <= 4) return `${число} сделки`
  return `${число} сделок`
}

/**
 * Разница словами — для подсказки над числом в таблице.
 *
 * Знак у `delta` смысла человеку не сообщает: «−35 565 150» одинаково читается и как
 * «недосчитались», и как «переплатили». Направление называем словом.
 */
export function направлениеРазницы(delta: number): string {
  if (delta > 0) return 'состав дороже суммы сделки'
  if (delta < 0) return 'состав дешевле суммы сделки'
  return 'сходится'
}
